import copy from 'copy-to-clipboard';
import { useState } from 'react';

import {
  Button,
  ButtonProps,
  Icon,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@eluve/components';
import { useTranslation } from '@eluve/i18n';

interface CopyTextButtonProps {
  copyText: string | (() => string);
  hideContentInTooltip?: boolean;
}

export const CopyTextButton: React.FC<ButtonProps & CopyTextButtonProps> = ({
  copyText,
  hideContentInTooltip = false,
  children,
  ...buttonProps
}) => {
  const { t } = useTranslation();
  const defaultTooltipContent =
    !hideContentInTooltip && typeof copyText === 'string'
      ? t('common.copyWithText', { text: copyText })
      : t('common.copyToClipboard');

  const [tooltipContent, setTooltipContent] = useState(defaultTooltipContent);
  const [open, setOpen] = useState(false);

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    const text = typeof copyText === 'function' ? copyText() : copyText;
    copy(text);
    setTooltipContent(t('common.copied'));
    setOpen(true);
    setTimeout(() => setTooltipContent(defaultTooltipContent), 3000);
    e.currentTarget.blur();
  };

  return (
    <Tooltip delayDuration={0} open={open} onOpenChange={setOpen}>
      <TooltipTrigger asChild>
        <Button
          variant="outline"
          onClick={handleClick}
          aria-label={defaultTooltipContent}
          {...buttonProps}
        >
          {children ?? <Icon name="Copy" />}
        </Button>
      </TooltipTrigger>
      <TooltipContent>{tooltipContent}</TooltipContent>
    </Tooltip>
  );
};
